import { Platform, StyleSheet, Text, View } from 'react-native'
import { colors, fontWeight, radius } from '../tokens'
import { trpc } from '../trpc'

type FileItem = { url: string; label: string }

type Props = {
  appealId: string
  files: ReadonlyArray<FileItem>
}

// The brief assumes the informal Band Review path; when the user moved in
// within the last 6 months they ALSO qualify for the formal VO 7455 route.
// Surface that as a quiet alternative above the stepper rather than another
// competing card — informal stays the recommended path.
export const FulfilmentFormalAlternative = ({ appealId, files }: Props) => {
  const recordChoice = trpc.appeal.recordPathChoice.useMutation()
  return (
    <View style={styles.card}>
      <Text style={styles.title}>Prefer the formal route?</Text>
      <Text style={styles.body}>
        You moved in within the last 6 months, so you also qualify for the formal Proposal route (VO
        7455). Lower success rate (~19% vs ~41%), but it forces a written decision and the right to
        escalate to a tribunal.
      </Text>
      <View style={styles.links}>
        {files.map((f) => (
          <FormalDownload
            key={f.url}
            url={f.url}
            label={f.label}
            onClick={() => recordChoice.mutate({ appealId, path: 'FORMAL_PROPOSAL' })}
          />
        ))}
      </View>
    </View>
  )
}

const FORMAL_FILENAMES: Record<string, string> = {
  'VO 7455 (official council tax proposal form)': 'vo-7455.pdf',
  'Evidence appendix': 'evidence-appendix.pdf',
}

// Same reasoning as Step 1's download: <a download> + server-side
// Content-Disposition triggers a real download instead of navigating away,
// so the user stays on the fulfilment screen for the next step.
const FormalDownload = ({
  url,
  label,
  onClick,
}: {
  url: string
  label: string
  onClick: () => void
}) =>
  Platform.OS === 'web' ? (
    <a
      data-testid="pdf-download"
      href={url}
      download={FORMAL_FILENAMES[label] ?? 'formal-proposal.pdf'}
      onClick={onClick}
      style={{
        color: colors.primary[700],
        fontWeight: 600,
        textDecoration: 'none',
        fontSize: 13.5,
      }}
    >
      ↓ {label}
    </a>
  ) : (
    <Text style={styles.linkText}>↓ {label}</Text>
  )

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.sm,
    padding: 18,
    paddingHorizontal: 20,
    marginBottom: 14,
    gap: 6,
  },
  title: { fontSize: 14, fontWeight: fontWeight.bold, color: colors.ink },
  body: { fontSize: 13, color: colors.muted, lineHeight: 19 },
  links: { marginTop: 8, gap: 6 },
  linkText: { color: colors.primary[700], fontWeight: '600', fontSize: 13.5 },
})
